import { Link } from "react-router-dom";
import { SITE } from "@/config/site";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import BrandLogo from "@/components/feature/BrandLogo";
import { useApiResource } from "@/hooks/useApiResource";
import { getCategories } from "@/services/publicApi";
import type { Category, SiteSocialLinks } from "@/types";

const exploreLinks = [
  { label: "Featured Listings", to: "/featured-listings" },
  { label: "All Listings", to: "/directory" },
  { label: "All Locations", to: "/locations" },
  { label: "Categories A–Z", to: "/categories-a-z" },
  { label: "Listing Plans", to: "/listing-plans" },
  { label: "Add Your Business", to: "/list-your-business" },
  { label: "About Us", to: "/about" },
  { label: "Contact Us", to: "/contact" },
  { label: "Terms & Conditions", to: "/terms" },
];

const helplines = [
  { number: "112", label: "All Emergency", icon: "ri-alarm-warning-line" },
  { number: "108", label: "Ambulance", icon: "ri-truck-line" },
  { number: "101", label: "Fire & Rescue", icon: "ri-fire-line" },
  { number: "100", label: "Police", icon: "ri-police-car-line" },
];

const legalLinks = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms of Service", to: "/terms-of-service" },
  { label: "Disclaimers on Ads", to: "/ad-disclaimer" },
  { label: "Refund Policy", to: "/refund-policy" },
];

const socialMeta: { key: keyof SiteSocialLinks; name: string; icon: string }[] = [
  { key: "facebook", name: "Facebook", icon: "ri-facebook-fill" },
  { key: "instagram", name: "Instagram", icon: "ri-instagram-line" },
  { key: "x", name: "X", icon: "ri-twitter-x-fill" },
  { key: "linkedin", name: "LinkedIn", icon: "ri-linkedin-fill" },
  { key: "youtube", name: "YouTube", icon: "ri-youtube-fill" },
];

export default function Footer() {
  const { settings } = useSiteSettings();
  const { data: liveCategories } = useApiResource<Category[]>(() => getCategories(), []);
  const popularSearchLinks = (liveCategories ?? []).slice(0, 20);
  const phoneHref = settings.contactPhone.replace(/[^\d+]/g, "");
  const socials = socialMeta.filter((s) => settings.social[s.key]);
  return (
    <footer>
      {/* Popular search strip */}
      <section className="border-y border-primary-100 bg-primary-50">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-search-line text-xl"></i>
              </span>
              <div className="min-w-0">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-foreground-950">
                  Popular Search
                </h2>
                <p className="text-xs text-foreground-600">Browse the most essential categories</p>
              </div>
            </div>
            <Link
              to="/categories"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-900 transition-colors hover:border-primary-400 hover:bg-primary-100 hover:text-primary-700 cursor-pointer"
            >
              View all categories
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
          </div>

          {popularSearchLinks.length > 0 && (
            <nav className="mt-4 flex flex-wrap gap-2.5">
              {popularSearchLinks.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.slug}`}
                  title={`${c.name} listings`}
                  className="group inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 py-1.5 pl-2.5 pr-3.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700 md:text-sm cursor-pointer"
                >
                  <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-100 text-primary-700 transition-colors duration-200 group-hover:bg-primary-500 group-hover:text-background-50">
                    <i className={`${c.icon || "ri-folder-3-line"} text-sm`}></i>
                  </span>
                  {c.name}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </section>

      {/* Emergency helplines band */}
      <section className="bg-[#8B1A1A]">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white">
                <i className="ri-alarm-warning-fill text-xs"></i>
                24×7 Emergency Helplines
              </span>
              <h2 className="mt-3 font-heading text-xl font-extrabold leading-tight text-white md:text-2xl">
                In an emergency, call first — then find help near you
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-white/70">
                These national numbers work from any phone, free of cost, across {SITE.regions.join(", ")}.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:w-auto">
              {helplines.map((h) => (
                <a
                  key={h.number}
                  href={`tel:${h.number}`}
                  title={`${h.label} — ${h.number}`}
                  className="group flex items-center gap-3 rounded-lg border border-white/20 bg-white/10 px-3.5 py-3 transition-colors hover:bg-white/20 cursor-pointer"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white text-red-700">
                    <i className={`${h.icon} text-base`}></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block font-heading text-base font-extrabold leading-none text-white">
                      {h.number}
                    </span>
                    <span className="mt-1 block truncate text-[11px] leading-none text-white/70">{h.label}</span>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main footer */}
      <div className="bg-[#04215E] text-primary-50">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-[3fr_2fr_2fr_3fr] lg:gap-x-6">
            <div className="lg:pr-12">
              <Link to="/" className="mb-4 flex items-center">
                <BrandLogo className="h-14" tone="dark" />
              </Link>
              <p className="max-w-sm text-sm leading-relaxed text-primary-100/90">
                The trusted hyperlocal business directory for the Mysore region. Discover, compare and connect with
                verified local businesses across {SITE.regions.join(", ")}.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {SITE.regions.map((r) => (
                  <Link
                    key={r}
                    to="/locations"
                    className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-primary-100 transition-colors hover:border-primary-400 hover:text-primary-300 cursor-pointer"
                  >
                    {r}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wide text-primary-300">Explore</h3>
              <ul className="space-y-2.5">
                {exploreLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-primary-100/90 transition-colors hover:text-primary-300">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wide text-primary-300">Policies</h3>
              <ul className="space-y-2.5">
                {legalLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-primary-100/90 transition-colors hover:text-primary-300">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wide text-primary-300">
                Get in Touch
              </h3>
              <ul className="space-y-3.5 text-sm text-primary-100/90">
                <li className="flex gap-3">
                  <i className="ri-mail-line mt-0.5 text-primary-300"></i>
                  <a href={`mailto:${settings.contactEmail}`} className="break-all hover:text-primary-300">
                    {settings.contactEmail}
                  </a>
                </li>
                <li className="flex gap-3">
                  <i className="ri-phone-line mt-0.5 text-primary-300"></i>
                  <a href={`tel:${phoneHref}`} className="hover:text-primary-300">
                    {settings.contactPhone}
                  </a>
                </li>
                <li className="flex gap-3">
                  <i className="ri-map-pin-2-line mt-0.5 text-primary-300"></i>
                  <span>{settings.address}</span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-file-text-line mt-0.5 text-primary-300"></i>
                  <span>GSTIN: {settings.gstin}</span>
                </li>
              </ul>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                {socials.map((s) => (
                  <a
                    key={s.key}
                    href={settings.social[s.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-primary-50 transition-colors hover:bg-primary-500 hover:text-foreground-950 cursor-pointer"
                  >
                    <i className={s.icon}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-4 py-6 text-xs text-primary-100/80 sm:flex-row sm:items-center sm:justify-between md:px-6">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <p className="whitespace-nowrap">
                © {new Date().getFullYear()} {settings.siteName}. All rights reserved.
              </p>
              <span className="hidden text-white/30 sm:inline" aria-hidden="true">|</span>
              <p className="whitespace-nowrap">
                Managed by:{" "}
                <a
                  href="https://www.mysoreitsolutions.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary-300 transition-colors hover:text-primary-200 cursor-pointer"
                >
                  Mysore IT Solutions
                </a>
              </p>
            </div>
            <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
              {legalLinks.map((l, i) => (
                <span key={l.to} className="flex items-center gap-x-2.5">
                  {i > 0 && <span className="text-white/30" aria-hidden="true">|</span>}
                  <Link
                    to={l.to}
                    className="whitespace-nowrap transition-colors hover:text-primary-300 cursor-pointer"
                  >
                    {l.label}
                  </Link>
                </span>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}